import cx from 'classnames';
import { canUseDOM } from 'exenv';
import _ from 'lodash';
import { Component, ReactElement } from 'react';
import { TRaidSummary } from '../../../../shared/types/raidTypes';
import { TSiteMetaProps, withSiteMeta } from '../../../../../../shared/frontend/HOCS/withSiteMeta';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import { TRaceGuildInfo, TRaceProgressTimelineInfo, TRegionSummary } from '../../libs/store';
import {
  dateOnly,
  factionClass,
  formatCountdownMsTime,
  formatMsShort,
  naturalShortDate,
  plural,
  relativeDate
} from '../../../../../../shared/frontend/libs/utils';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { Link } from '../../../../../../shared/frontend/components/link';
import CountdownTimer from '../../../../../../shared/frontend/components/presentational/countdownTimer';
import CountUpTimer from './countUpTimer';
import { getRaidLockState } from './raceProgressTimeline';
import { ProgressBadge } from './raidRankingsElements/ProgressBadge';
import { StreamTooltipContent } from '../../../../../../shared/frontend/components/presentational/streamPreview';
import { ComplexTooltip, SimpleTooltip_NEXT } from '../../../../../../shared/frontend/components/presentational/tooltip';

type TWorldFirstTimelineNodeProps = {
  raid: TRaidSummary;
  region: TRegionSummary;
  difficulty: string;
  node: TRaceProgressTimelineInfo;
  total: number;
  align: 'top' | 'bottom';
  faction?: string;
  maxGuildsPerNode?: number;
};

const REGION_LOOKUP: any = {
  us: 'Americas',
  eu: 'Europe',
  kr: 'Korea',
  tw: 'Taiwan',
  cn: 'China'
};

function WorldFirstTimelineNode(props: TWorldFirstTimelineNodeProps) {
  const visibleGuilds =
    props.maxGuildsPerNode && props.maxGuildsPerNode < 3
      ? props.node.guilds.slice(0, props.maxGuildsPerNode)
      : props.node.guilds;

  return (
    <div className={cx('rio-worldfirst-node', { empty: visibleGuilds.length === 0 })}>
      <div className="rio-worldfirst-node--guilds">
        {visibleGuilds.map((guildInfo: TRaceGuildInfo, index: number) => (
          <p key={index}>
            <ComplexTooltip
              placement="top"
              tooltip={
                <div>
                  <div className="slds-text-body--small slds-text-align--center">
                    <p className={`rio-faction-color--${guildInfo.guild.faction}`}>
                      <b>&lt;{guildInfo.guild.name}&gt;</b>
                    </p>
                    <p>
                      <b>{guildInfo.guild.realm.name}</b>
                    </p>
                    {guildInfo.guild.region.slug !== 'cn' ? (
                      <>
                        <p>{naturalShortDate(guildInfo.defeatedAt.toString())}</p>
                        <p>({relativeDate(guildInfo.defeatedAt.toString())})</p>
                      </>
                    ) : (
                      <>
                        <p>{dateOnly(guildInfo.defeatedAt.toString())}</p>
                        <p>({relativeDate(guildInfo.defeatedAt.toString())})</p>
                      </>
                    )}
                  </div>
                  {guildInfo.streamers.count > 0 && (
                    <StreamTooltipContent
                      type="twitch"
                      stream={guildInfo.streamers.stream}
                      count={guildInfo.streamers.count}
                    />
                  )}
                </div>
              }
            >
              <span className={guildInfo.streamers.count > 0 ? 'streaming-now' : ''}>
                <Link
                  className={cx('rio-worldfirst-node--guild-text', factionClass(guildInfo.guild.faction))}
                  to={resolveRoutePath(namedRoutes.GuildDetails, {
                    region: guildInfo.guild.region.slug,
                    realm: guildInfo.guild.realm.slug,
                    guild: guildInfo.guild.name
                  })}
                >
                  {guildInfo.guild.name}
                </Link>
              </span>
            </ComplexTooltip>
          </p>
        ))}
      </div>
    </div>
  );
}

export type TWorldFirstTimeline = {
  difficulty: string;
  timeline: TRaceProgressTimelineInfo[];
  raid: TRaidSummary;
  region: TRegionSummary;
  faction?: string;
  maxGuildsPerNode?: number;
  overrideContainerHeight?: number;
};

function WorldFirstTimeline(props: TWorldFirstTimeline) {
  const containerHeight = props.overrideContainerHeight || 250;

  if (!props.timeline || props.timeline.length === 0) {
    return <div style={{ height: containerHeight }} />;
  }

  const nodesWithGuilds = props.timeline.filter((p) => p.totalGuilds > 0);
  const maxProgress = nodesWithGuilds.length > 0 ? _.last<any>(nodesWithGuilds).progress : 0;
  const topGuild = maxProgress ? props.timeline[maxProgress - 1].guilds[0] : null;

  return (
    <div>
      <div className="rio-worldfirst-top-container">
        {props.timeline.map((node: TRaceProgressTimelineInfo, index: any) => (
          <div key={index} className="rio-worldfirst-top-node">
            <div className={`rio-worldfirst-top-node-spike ${maxProgress > index ? 'completed' : ''}`}>
              {index + 1}/{props.timeline.length}
            </div>
          </div>
        ))}
      </div>
      <div className="rio-worldfirst-bar-container">
        {props.timeline.map((node: TRaceProgressTimelineInfo, index: number) => (
          <SimpleTooltip_NEXT
            key={index}
            hideTooltip={node.totalGuilds === 0}
            tag="div"
            tooltip={`${node.totalGuilds} ${plural('guild', node.totalGuilds)} at ${node.progress}/${
              props.timeline.length
            } ${_.capitalize(props.difficulty.substr(0, 1).toUpperCase())}`}
            className={cx('rio-worldfirst-bar-node', {
              'rio-worldfirst-bar--filled': maxProgress > index,
              [`rio-worldfirst-bar--${topGuild ? topGuild.guild.faction : ''}`]: !!topGuild,
              'end-of-bar': maxProgress === index + 1
            })}
          >
            <div className={'rio-worldfirst-bar-node-spike'}>
              <i className="fa fa-circle" />
            </div>
          </SimpleTooltip_NEXT>
        ))}
      </div>
      <div className="rio-worldfirst-guilds-container rio-hidden--small">
        {props.timeline.map((node: TRaceProgressTimelineInfo, index: number) => (
          <WorldFirstTimelineNode
            key={node.progress}
            difficulty={props.difficulty}
            node={node}
            total={props.timeline.length}
            align="top"
            raid={props.raid}
            region={props.region}
            faction={props.faction}
            maxGuildsPerNode={props.maxGuildsPerNode}
          />
        ))}
      </div>
      {topGuild && (
        <div className="slds-p-around--large slds-text-align--center rio-takeover-hidden rio-hidden--medium rio-hidden--large rio-hidden--x-large">
          <Link
            to={topGuild.guild.path}
            className={`slds-text-heading--medium rio-realm-link rio-faction-color--${topGuild.guild.faction}`}
          >
            &lt;{topGuild.guild.name}&gt;
          </Link>

          {maxProgress !== props.timeline.length && (
            <div>
              {maxProgress}/{props.timeline.length} {props.difficulty.toUpperCase().charAt(0)} For{' '}
              <CountUpTimer
                style={{ display: 'inline-block' }}
                from={topGuild.defeatedAt.toString()}
                formatter={formatMsShort}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export type TWorldFirstTimelineContainerProps = TSiteMetaProps & {
  region: TRegionSummary;
  difficulty: string;
  raid: TRaidSummary;
  timeline: TRaceProgressTimelineInfo[];
  faction?: string;
  ordinal: number;
  className?: string;
  showWorldFirstRegionTracker?: boolean;
  forceBackgroundUrl?: string;
  forceTitle?: string;
};

export default withSiteMeta(
  class extends Component<TWorldFirstTimelineContainerProps, any> {
    public render(): ReactElement<TWorldFirstTimelineContainerProps> {
      const raidLockState = getRaidLockState(this.props.region.slug, this.props.raid.slug, this.props.difficulty);
      const nodesWithGuilds = this.props.timeline.filter((p) => p.totalGuilds > 0);
      const maxProgress = nodesWithGuilds.length > 0 ? _.last<any>(nodesWithGuilds).progress : 0;
      const topGuild = maxProgress ? this.props.timeline[maxProgress - 1].guilds[0] : null;
      const slatIndex = 1 + this.props.ordinal;

      if (maxProgress > 0) {
        raidLockState.locked = false;
      }

      if (raidLockState.locked) {
        return (
          <div
            className={cx('rio-worldfirst-timeline', this.props.siteConfig.isEmbedded ? 'embedded' : '')}
            style={{
              background: `url(${asset(
                this.props.forceBackgroundUrl ||
                  `/images/${this.props.raid.slug}/headers/worldfirst/slat_${slatIndex}.jpg`,
                {
                  width: 1000,
                  quality: 75
                }
              )})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center center'
            }}
          >
            <div className="slds-text-heading--large slds-p-around--xx-large slds-text-align--center">
              <div className="d-inline-block slds-m-top--small">
                {this.presentableRegionName(this.props.region.slug)} unlocks in{' '}
                <CountdownTimer
                  style={{ display: 'inline-block' }}
                  to={raidLockState.timestamp}
                  formatter={formatCountdownMsTime}
                  onComplete={() => {
                    if (canUseDOM) {
                      window.location.reload.bind(window.location);
                    }
                  }}
                />
              </div>
              {!this.props.siteConfig.isEmbedded && (
                <div>
                  {this.props.region.slug !== 'cn' ? (
                    <Link
                      to={resolveRoutePath(namedRoutes.RaidRankings, {
                        raid: this.props.raid.slug,
                        region: this.props.region.slug,
                        difficulty: 'heroic'
                      })}
                      className="rio-realm-link slds-text-body--small"
                    >
                      [view heroic leaderboard]
                    </Link>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      }

      return (
        <div
          className={cx(
            'rio-worldfirst-timeline slds-p-around--x-small',
            this.props.className,
            this.props.siteConfig.isEmbedded ? 'embedded' : ''
          )}
          style={{
            background:
              'linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)),' +
              `url(${asset(
                this.props.forceBackgroundUrl ||
                  `/images/${this.props.raid.slug}/headers/worldfirst/slat_${slatIndex}.jpg`,
                {
                  width: 1000,
                  quality: 75
                }
              )})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center'
          }}
        >
          <div className="slds-text-heading--medium slds-m-bottom--x-small slds-clearfix">
            <div className="slds-float--left">
              <b>{this.props.forceTitle || this.presentableRegionName(this.props.region.slug)}</b>{' '}
              {!this.props.siteConfig.isEmbedded && (
                <>
                  {this.props.showWorldFirstRegionTracker && (
                    <Link
                      to={resolveRoutePath(namedRoutes.RaidWorldFirstTracker, {
                        raid: this.props.raid.slug
                      })}
                      className="slds-m-left--x-small slds-text-body--small"
                    >
                      [all regions]
                    </Link>
                  )}{' '}
                  <Link
                    to={resolveRoutePath(namedRoutes.RaidRankings, {
                      raid: this.props.raid.slug,
                      region: this.props.region.slug,
                      difficulty: this.props.difficulty
                    })}
                    className="slds-m-left--x-small slds-text-body--small"
                  >
                    [leaderboard]
                  </Link>{' '}
                  <Link
                    to={resolveRoutePath(namedRoutes.RaidHallOfFame, {
                      raid: this.props.raid.slug,
                      region: this.props.region.slug,
                      difficulty: this.props.difficulty
                    })}
                    className="slds-m-left--x-small slds-text-body--small"
                  >
                    [hall of fame]
                  </Link>
                </>
              )}
            </div>
            <div className="slds-float--right">
              {topGuild && maxProgress !== this.props.timeline.length && (
                <SimpleTooltip_NEXT
                  className="rio-hidden--small"
                  tag="span"
                  tooltip={
                    this.props.region.slug === 'world'
                      ? 'Time since the top guild in the world reached their current boss'
                      : 'Time since the top guild in this region reached their current boss'
                  }
                >
                  <span className={`slds-text-body--small ${maxProgress > 0 ? 'slds-m-right--small' : ''}`}>
                    <span>
                      <CountUpTimer
                        style={{ display: 'inline-block' }}
                        from={topGuild.defeatedAt.toString()}
                        formatter={formatMsShort}
                      />{' '}
                      Since Last Boss
                    </span>
                  </span>
                </SimpleTooltip_NEXT>
              )}

              <SimpleTooltip_NEXT
                tag="span"
                tooltip={
                  this.props.region.slug === 'world'
                    ? 'Highest progress earned by any guild in the world'
                    : 'Highest progress earned by a guild in this region'
                }
              >
                {maxProgress > 0 && (
                  <ProgressBadge
                    numDefeated={maxProgress}
                    totalBosses={this.props.timeline.length}
                    difficulty={this.props.difficulty}
                    visible={true}
                    color="light"
                    size="small"
                  />
                )}
              </SimpleTooltip_NEXT>
            </div>
          </div>

          <div className="rio-worldfirst-container slds-m-top--large">
            <WorldFirstTimeline
              difficulty={this.props.difficulty}
              timeline={this.props.timeline}
              raid={this.props.raid}
              region={this.props.region}
              faction={this.props.faction}
              maxGuildsPerNode={3}
            />
          </div>
        </div>
      );
    }

    private presentableRegionName(slug: string) {
      return REGION_LOOKUP[slug] || slug;
    }
  }
);
